import React from "react";
import { ExternalLink, BookOpen, Check } from "lucide-react";

const AMAZON_URL = "https://www.amazon.com/MindWorks-Daily-Battle-Plan-Anguiano/dp/B0H8MWQ27Z/ref=mp_s_a_1_1?crid=2Y03LP5WREXUE&dib=eyJ2IjoiMSJ9.DVvOth0JXnPYVxqQK_qomSYNG2CnkzpcDWCC7XgzMBvGjHj071QN20LucGBJIEps.S545zcigGsbwdmDyOfPbWoewS0PwTCcN-3BqeeJOUl0&dib_tag=se&keywords=mindworks+daily+battle+plan&qid=1784830935&sprefix=mindworks+daily%2Caps%2C254&sr=8-1";
const HERO_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/journal/cover-hero`;
const TIM_WITH_BOOK = `${process.env.REACT_APP_BACKEND_URL}/api/journal/tim-with-book`;

// The journal has more than 4 daily prompts (mission, priorities, mental
// state, focus, discipline, obstacles, wins, learnings, tomorrow…). Rather
// than list them all, we highlight a handful so the page feels honest
// without over-promising a fixed structure. Copy is intentionally sparse
// per Tim: don't oversell the internals — sell the FIGHT.
const INSIDE_ITEMS = [
    "Today's Mission",
    "Top 3 Priorities",
    "Mental State Check",
    "Focus",
    "Discipline",
    "Obstacles",
    "Wins & setbacks worth learning from",
    "…and more",
];

export default function Journal() {
    return (
        <div className="bg-black text-white min-h-screen" data-testid="journal-page">
            {/* HERO */}
            <section
                className="relative overflow-hidden border-b border-white/10"
                data-testid="journal-hero"
            >
                <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-red-800/20 blur-3xl rounded-full pointer-events-none" />
                <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-amber-700/10 blur-3xl rounded-full pointer-events-none" />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    {/* Copy */}
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-500/40 bg-amber-500/5 font-stencil text-[10px] tracking-[0.3em] text-amber-400 mb-6">
                            <BookOpen size={12} /> NEW · PAPERBACK · ON AMAZON
                        </div>

                        <h1
                            className="font-display uppercase tracking-tighter leading-[0.85] text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
                            data-testid="journal-headline"
                        >
                            The MindWorks<br />
                            <span className="text-red-600">Daily Battle Plan.</span>
                        </h1>

                        <p className="mt-6 font-display uppercase tracking-tight text-lg md:text-xl text-white/85">
                            A daily battle plan for <span className="text-red-500">a rebuilt mind.</span>
                        </p>

                        <p className="mt-6 max-w-xl text-white/70 leading-relaxed">
                            Most people don&apos;t stay stuck because they don&apos;t care. They stay stuck because they don&apos;t have a plan. This journal is the plan.
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-5">
                            <a
                                href={AMAZON_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-3 px-7 py-4 bg-amber-500 hover:bg-amber-400 text-black font-display tracking-[0.12em] uppercase transition-colors shadow-[0_0_60px_-15px_rgba(245,158,11,0.7)]"
                                data-testid="journal-amazon-cta-hero"
                            >
                                <ExternalLink size={18} />
                                <span className="flex items-baseline gap-3">
                                    <span className="text-sm">Get it on Amazon</span>
                                    <span className="font-display text-2xl md:text-3xl leading-none">$12.99</span>
                                </span>
                            </a>
                            <div className="text-white/50 text-xs font-stencil tracking-widest">
                                PAPERBACK · SHIPS FROM AMAZON
                            </div>
                        </div>
                    </div>

                    {/* Product photo */}
                    <div className="lg:col-span-5 order-1 lg:order-2">
                        <a
                            href={AMAZON_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block group relative"
                            data-testid="journal-hero-image-link"
                            aria-label="Buy on Amazon"
                        >
                            <div
                                className="absolute -inset-2 bg-red-700/25 -z-10 group-hover:bg-red-700/45 transition-colors"
                                style={{ transform: "translate(14px, 14px)" }}
                            />
                            <img
                                src={HERO_IMG}
                                alt="MindWorks Daily Battle Plan Journal — paperback on a wooden desk"
                                className="w-full h-auto block border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.95)] group-hover:-translate-y-1 transition-transform duration-300"
                                loading="eager"
                                data-testid="journal-hero-image"
                            />
                        </a>
                    </div>
                </div>
            </section>

            {/* MANIFESTO */}
            <section className="border-b border-white/10 bg-[#050505] py-20 md:py-28" data-testid="journal-manifesto">
                <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
                    <div className="flex items-center justify-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" />
                        Fight With Intention
                        <span className="h-px w-12 bg-red-600" />
                    </div>
                    <h2 className="font-display uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl">
                        You don&apos;t win<br />
                        by <span className="text-red-600">hoping.</span>
                    </h2>
                    <p className="mt-8 text-white/75 leading-relaxed text-lg">
                        You win by showing up with a plan.
                    </p>
                    <p className="mt-6 text-white/65 leading-relaxed">
                        Every day you&apos;ll check your mental state, identify today&apos;s mission, celebrate small wins, learn from setbacks, and ask the two questions that create real growth:
                    </p>
                    <div className="mt-6 border-l-2 border-red-600/60 pl-6 py-2 max-w-md mx-auto text-left space-y-2">
                        <p className="font-display uppercase tracking-tight text-white text-lg md:text-xl">What will I try again?</p>
                        <p className="font-display uppercase tracking-tight text-white text-lg md:text-xl">What will make tomorrow even better?</p>
                    </div>
                    <p className="mt-8 text-white/65 leading-relaxed">
                        Transformation rarely happens in one dramatic moment. It happens through small, faithful decisions repeated day after day.
                    </p>
                </div>
            </section>

            {/* WHAT'S INSIDE */}
            <section className="border-b border-white/10 py-20 md:py-28" data-testid="journal-inside">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="text-center mb-12">
                        <div className="flex items-center justify-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                            <span className="h-px w-12 bg-red-600" />
                            What&apos;s Inside
                            <span className="h-px w-12 bg-red-600" />
                        </div>
                        <h2 className="font-display uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl">
                            The tools of the drill.
                        </h2>
                    </div>
                    <ul className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                        {INSIDE_ITEMS.map((item, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-3 text-white/85 text-base border border-white/10 bg-[#0a0a0a] px-5 py-4 hover:border-red-700/60 transition-colors"
                                data-testid={`journal-inside-item-${i}`}
                            >
                                <Check size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* FINAL CTA — real photo of Tim holding the actual journal */}
            <section className="py-20 md:py-28 border-b border-white/10" data-testid="journal-final-cta">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
                    {/* LEFT — authentic selfie */}
                    <a
                        href={AMAZON_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block group relative mx-auto md:mx-0 max-w-sm w-full"
                        data-testid="journal-tim-photo-link"
                        aria-label="Buy on Amazon"
                    >
                        <div
                            className="absolute -inset-2 bg-red-700/25 -z-10 group-hover:bg-red-700/45 transition-colors"
                            style={{ transform: "translate(10px, 10px)" }}
                        />
                        <img
                            src={TIM_WITH_BOOK}
                            alt="Tim holding the MindWorks Daily Battle Plan Journal in his kitchen"
                            className="w-full h-auto block border border-white/10 shadow-[0_25px_80px_-20px_rgba(0,0,0,0.9)] group-hover:-translate-y-1 transition-transform duration-300"
                            loading="lazy"
                            data-testid="journal-tim-photo"
                        />
                        <div className="mt-3 text-center text-[10px] font-stencil tracking-[0.3em] text-white/45">
                            NOT A MOCKUP · REAL BOOK · REAL AUTHOR
                        </div>
                    </a>

                    {/* RIGHT — closing pitch + CTA */}
                    <div className="text-center md:text-left">
                        <h3 className="font-display uppercase tracking-tighter leading-tight text-3xl md:text-5xl">
                            Ready to <span className="text-red-600">show up</span>?
                        </h3>
                        <p className="mt-6 text-white/70 leading-relaxed">
                            The battle for your mind isn&apos;t won in a weekend. It&apos;s won page by page, morning by morning. Start tomorrow.
                        </p>

                        <div className="mt-8 flex flex-wrap items-center justify-center md:justify-start gap-4">
                            <a
                                href={AMAZON_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-3 px-8 py-4 bg-amber-500 hover:bg-amber-400 text-black font-display tracking-[0.12em] uppercase transition-colors shadow-[0_0_60px_-15px_rgba(245,158,11,0.7)]"
                                data-testid="journal-amazon-cta-final"
                            >
                                <ExternalLink size={18} />
                                <span className="flex items-baseline gap-3">
                                    <span className="text-sm">Get it on Amazon</span>
                                    <span className="font-display text-2xl md:text-3xl leading-none">$12.99</span>
                                </span>
                            </a>
                        </div>

                        <p className="mt-8 text-[11px] text-white/40 italic max-w-md">
                            &quot;Transformation doesn&apos;t happen by accident — it happens through daily repetition.&quot; — Tim
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
